import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store/index';
import { CreateElement } from 'vue/types/umd';
import tipsStore from './store/modules/tips.store';
import { i18n } from '@/localization';
Vue.use(Router);

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,

    /*
     * | ------------------------
     * | Simulate native-like scroll behavior when navigating to a new
     * | route and using back/forward buttons.
     * | ------------------------
     * |
     */
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        } else {
            return { x: 0, y: 0 };
        }
    },

    /*
     * | ------------------------
     * | All router page routes
     * | Contains all routes and their matching components
     * | ------------------------
     * |
     */
    routes: [
        {
            path: '/',
            name: 'guest-or-auth',
            beforeEnter: guardGuestOrAuth,
            component: loadView('guestOrAuth'),
            meta: {
                title: 'Welkom bij Start To Stop',
            },
        },
        {
            path: '/guest/welcome',
            name: 'welcome',
            beforeEnter: guardGuest,
            component: loadView('guest/authenticate'),
            meta: {
                title: 'Welkom bij Start To Stop',
                type: 'welcome',
                group: 'guest',
            },
        },
        {
            path: '/guest/login',
            name: 'login',
            beforeEnter: guardGuest,
            component: loadView('guest/authenticate'),
            meta: {
                title: 'Login | Start to Stop',
                type: 'login',
                group: 'guest',
            },
        },
        {
            path: '/guest/register',
            name: 'register',
            beforeEnter: guardGuest,
            component: loadView('guest/authenticate'),
            meta: {
                title: 'Registreer | Start to Stop',
                type: 'register',
                group: 'guest',
            },
        },
        {
            path: '/guest/invite/:token',
            name: 'invite',
            beforeEnter: guardGuest,
            component: loadView('guest/authenticate'),
            meta: {
                title: 'Registreer | Start to Stop',
                type: 'register',
                group: 'guest',
            },
        },
        {
            path: '/guest/email-confirm',
            name: 'email-confirm',
            beforeEnter: guardGuest,
            component: loadView('guest/authenticate'),
            meta: {
                title: 'Verifieer je account | Start to Stop',
                type: 'email-confirm',
                group: 'guest',
            },
        },
        {
            path: '/guest/password-forgot',
            name: 'password-forgot',
            beforeEnter: guardGuest,
            component: loadView('guest/authenticate'),
            meta: {
                title: 'Wachtwoord vergeten | Start to Stop',
                type: 'password-forgot',
                group: 'guest',
            },
        },
        {
            path: '/guest/password-reset',
            name: 'password-reset',
            beforeEnter: guardGuest,
            component: loadView('guest/authenticate'),
            meta: {
                title: 'Wachtwoord resetten | Start to Stop',
                type: 'password-reset',
                group: 'guest',
            },
        },

        /*
        | --------------------
        | AUTH Tour
        | --------------------
        | Shows an onboarding tour
        */
        {
            path: '/auth/tour',
            name: 'tour',
            beforeEnter: guardAuth,
            component: loadView('auth/tour'),
            meta: {
                title: 'Welcome plan | Start to Stop',
                group: 'auth',
            },
        },

        {
            path: '/auth/email-confirm',
            name: 'auth-email-confirm',
            beforeEnter: guardAuth,
            component: loadView('auth/authenticate'),
            meta: {
                title: 'Verifieer je account | Start to Stop',
                type: 'email-confirm',
                group: 'auth',
            },
        },

        /*
        | --------------------
        | AUTH PROFILE
        | --------------------
        | Current user profile pages and badges
        */
        {
            path: '/auth/profile',
            name: 'profile',
            beforeEnter: guardAuth,
            component: loadView('auth/profile/profile'),
            meta: {
                title: 'Profiel | Start to Stop',
                group: 'auth',
            },
        },
        {
            path: '/auth/settings',
            name: 'settings',
            beforeEnter: guardAuth,
            component: loadView('auth/profile/settings'),
            meta: {
                title: 'Instellingen | Start to Stop',
                group: 'auth',
            },
        },
        {
            path: '/auth/permissions',
            name: 'permissions',
            beforeEnter: guardAuth,
            component: loadView('auth/profile/permissions'),
            meta: {
                title: 'Toestemming | Start to Stop',
                group: 'auth',
            },
        },
        {
            path: '/auth/unsubscribe',
            name: 'unsubscribe',
            beforeEnter: guardAuth,
            component: loadView('auth/profile/unsubscribe'),
            meta: {
                title: 'Uitschrijven | Start to Stop',
                group: 'auth',
            },
        },
        {
            path: '/auth/notifications',
            name: 'notifications',
            beforeEnter: guardAuth,
            component: loadView('auth/profile/notifications'),
            meta: {
                title: 'Meldingen | Start to Stop',
                group: 'auth',
            },
        },
        {
            path: '/auth/password-update',
            name: 'password-update',
            beforeEnter: guardAuth,
            component: loadView('auth/profile/password-update'),
            meta: {
                title: 'Wachtwoord opnieuw instellen | Start to Stop',
                group: 'auth',
            },
        },


        /*
        | --------------------
        | DASHBOARD
        | --------------------
        | Current user home with dashboard items
        */
        {
            path: '/auth/dashboard',
            name: 'dashboard',
            beforeEnter: guardAuth,
            component: loadView('auth/dashboard/index'),
            meta: {
                title: 'Dashboard | Start to Stop',
                group: 'auth',
                color: '#00a3ff',
                beforeResolve(to: any, from: any, next: any) {
                    store.dispatch('startLoadingPage');

                    const auth: Auth = store.getters['user/auth'];
                    store.dispatch('user/readStatistics', { userId: auth.user_id }).then((data) => {

                        // @ts-ignore
                        store.dispatch('tips/spotlight').then((tip: Tip) => {
                            to.params.tip = tip;
                            next();
                            store.dispatch('stopLoadingPage');
                        });
                    });
                },
            },
        },

        /*
        | --------------------
        | TIPS
        | --------------------
        | Current user tips and tip detail pages
        */
        {
            path: '/auth/tips',
            alias: '/tips',
            name: 'tips',
            beforeEnter: guardAuth,
            component: loadView('auth/tips/tips'),
            meta: {
                title: 'Tips | Start to Stop',
                group: 'auth',
                color: '#29cd60',
                beforeResolve(to: any, from: any, next: any) {
                    store.dispatch('startLoadingPage');

                    const auth: Auth = store.getters['user/auth'];
                    store.dispatch('user/readStatistics', { userId: auth.user_id }).then((data: DashboardData) => {
                        store.dispatch('tips/spotlight').then((tip: Tip) => {
                            to.params.tip = tip;

                            store.dispatch('tips/search', { show_in_overview: true, exclude_id: tip.id, level: data.current_level, show_tip: true })
                                .then(() => next())
                                .finally(() => {
                                    store.dispatch('stopLoadingPage');
                                });
                        });
                    });

                },
            },
        },
        {
            path: '/auth/tips/level/:level',
            name: 'tips-by-level',
            beforeEnter: guardAuth,
            component: loadView('auth/tips/tip-level'),
            meta: {
                title: 'Tips by level | Start to Stop',
                group: 'auth',
                color: '#29cd60',
                beforeResolve(to: any, from: any, next: any) {
                    store.dispatch('startLoadingPage');

                    store.dispatch('tips/search', { show_in_overview: true, level: to.params.level })
                        .then(() => next())
                        .finally(() => {
                            store.dispatch('stopLoadingPage');
                        });

                },
            },
        },
        {
            path: '/auth/tips/:id',
            alias: '/tips/:id',
            name: 'tips-detail',
            beforeEnter: guardAuth,
            component: loadView('auth/tips/tips-detail'),
            meta: {
                title: 'Tips | Start to Stop',
                group: 'auth',
                color: '#29cd60',
                beforeResolve(to: any, from: any, next: any) {
                    store.dispatch('startLoadingPage');

                    next();

                    store.dispatch('tips/read', to.params.id).finally(() => {
                        store.dispatch('stopLoadingPage');
                    });
                },
            },
        },
        {
            path: '/auth/tips/category/:slug',
            name: 'tips-by-category',
            beforeEnter: guardAuth,
            component: loadView('auth/tips/tips-category'),
            meta: {
                title: 'Tips | Start to Stop',
                group: 'auth',
                color: '#29cd60',
                beforeResolve(to: any, from: any, next: any) {
                    store.dispatch('startLoadingPage');

                    next();

                    store.dispatch('tips/index', to.params.slug).finally(() => {
                        store.dispatch('stopLoadingPage');
                    });
                },
            },
        },

        /*
        | --------------------
        | CHAT
        | --------------------
        | Landbot chat pages
        */
        {
            path: '/auth/chat',
            name: 'chat',
            beforeEnter: guardAuth,
            component: loadView('auth/chat'),
            meta: {
                title: 'Q & A | Start to Stop',
                group: 'auth',
                color: '#f95950',
                beforeResolve(to: any, from: any, next: any) {
                    store.dispatch('startLoadingPage');

                    const auth: Auth = store.getters['user/auth'];
                    store.dispatch('user/readStatistics', { userId: auth.user_id }).then((data) => {
                        next();
                        store.dispatch('stopLoadingPage');
                    });
                },
            },
        },

        /*
        | --------------------
        | TIMELINE
        | --------------------
        | Current user timeline
        */
        {
            path: '/auth/timeline',
            name: 'timeline',
            beforeEnter: guardAuth,
            component: loadView('auth/timeline/index'),
            meta: {
                title: 'Tijdlijn | Start to Stop',
                group: 'auth',
                color: '#f9c22e',
                beforeResolve(to: any, from: any, next: any) {
                    store.dispatch('startLoadingPage');

                    const auth: Auth = store.getters['user/auth'];
                    store.dispatch('user/readStatistics', { userId: auth.user_id })
                        .then(() => next())
                        .catch((err: ErrorResponse) => handlePageError(err, next))
                        .finally(() => store.dispatch('stopLoadingPage'));
                },
            },
        },

        {
            path: '/auth/do-the-test',
            name: 'do-the-test',
            beforeEnter: guardAuth,
            component: loadView('auth/do-the-test'),
            meta: {
                title: 'Doe de test | Start to Stop',
                group: 'auth',
                color: '#f9c22e',
                beforeResolve(to: any, from: any, next: any) {
                    store.dispatch('startLoadingPage');

                    next();

                    const auth: Auth = store.getters['user/auth'];
                    store.dispatch('user/readStatistics', { userId: auth.user_id }).finally(() => {
                        store.dispatch('stopLoadingPage');
                    });
                },
            },
        },
        {
            path: '/auth/test-results/:slug',
            name: 'test-results',
            beforeEnter: guardAuth,
            component: loadView('auth/test-results'),
            meta: {
                title: 'Mijn testresultaten | Start to Stop',
                group: 'auth',
                color: '#f9c22e',
                beforeResolve(to: any, from: any, next: any) {
                    store.dispatch('startLoadingPage');

                    const auth: Auth = store.getters['user/auth'];
                    store.dispatch('user/readStatistics', { userId: auth.user_id }).finally(() => {
                        next();
                        store.dispatch('stopLoadingPage');
                    });
                },
            },
        },
        {
            path: '/auth/upgrade-plan',
            name: 'upgrade-plan',
            beforeEnter: guardAuth,
            component: loadView('auth/profile/upgrade-plan'),
            meta: {
                title: 'Mijn schema | Start to Stop',
                group: 'auth',
                color: '#fffff',
                beforeResolve(to: any, from: any, next: any) {
                    store.dispatch('startLoadingPage');

                    const auth: Auth = store.getters['user/auth'];
                    store.dispatch('user/readStatistics', { userId: auth.user_id }).finally(() => {
                        next();
                        store.dispatch('stopLoadingPage');
                    });
                },
            },
        },

        /*
        | --------------------
        | ADMIN
        | --------------------
        | ADMIN dashboarding
        */
        {
            path: '/admin/dashboard',
            name: 'admin-dashboard',
            beforeEnter: guardAuth,
            component: loadView('admin/dashboard'),
            meta: {
                title: 'Dashboard | Start to Stop',
                group: 'admin',
                color: '#fffff',
            },
        },
        {
            path: '/admin/clients',
            name: 'admin-clients',
            beforeEnter: guardAuth,
            component: loadView('admin/clients'),
            meta: {
                title: 'Clients | Start to Stop',
                group: 'admin',
                color: '#fffff',
            },
        },
        {
            path: '/admin/clients/:id',
            name: 'admin-clients-detail',
            beforeEnter: guardAuth,
            component: loadView('admin/clients-detail'),
            meta: {
                title: 'Clients | Start to Stop',
                group: 'admin',
                color: '#fffff',
                beforeResolve(to: any, from: any, next: any) {
                    store.dispatch('timeline/read', { userId: to.params.id });
                    store.dispatch('user/resetStatistics');
                    store.dispatch('user/readStatistics', { userId: to.params.id });
                    store.dispatch('dashboard/readClient', { client_id: to.params.id }).then(() => {
                        next();
                    });
                },
            },
        },
        {
            path: '/admin/consultants',
            name: 'admin-consultants',
            beforeEnter: guardAuth,
            component: loadView('admin/consultants'),
            meta: {
                title: 'Consultants | Start to Stop',
                group: 'admin',
            },
        },
        {
            path: '/admin/consultants/:id',
            name: 'admin-consultants-detail',
            beforeEnter: guardAuth,
            component: loadView('admin/consultants-detail'),
            meta: {
                title: 'Consultants | Start to Stop',
                group: 'admin',
            },
        },
        {
            path: '/admin/settings',
            name: 'admin-settings',
            beforeEnter: guardAuth,
            component: loadView('admin/settings'),
            meta: {
                title: 'Settings | Start to Stop',
                group: 'admin',
            },
        },
        {
            path: '/admin/tips',
            name: 'admin-tips',
            beforeEnter: guardAuth,
            component: loadView('admin/tips'),
            meta: {
                title: 'Tips | Start to Stop',
                group: 'admin',
                beforeResolve(to: any, from: any, next: any) {
                    store.dispatch('startLoadingPage');

                    next();

                    store.dispatch('tips/index').finally(() => {
                        store.dispatch('stopLoadingPage');
                    });
                },
            },
        },
        {
            path: '/admin/tips/create',
            name: 'admin-tips-create',
            beforeEnter: guardAuth,
            component: loadView('admin/tips-detail'),
            meta: {
                title: 'Tips create | Start to Stop',
                group: 'admin',
                beforeResolve(to: any, from: any, next: any) {
                    store.dispatch('startLoadingPage');

                    next();

                    store.dispatch('tips/clearViewingTip').finally(() => {
                        store.dispatch('stopLoadingPage');
                    });
                },
            },
        },
        {
            path: '/admin/tips/:id',
            name: 'admin-tips-update',
            beforeEnter: guardAuth,
            component: loadView('admin/tips-detail'),
            meta: {
                title: 'Tips | Start to Stop',
                group: 'admin',
                beforeResolve(to: any, from: any, next: any) {
                    store.dispatch('startLoadingPage');

                    const p1 = store.dispatch('tips/read', to.params.id);
                    const p2 = store.dispatch('tests/index');
                    const p3 = store.dispatch('levels/index');

                    Promise.all([p1, p2, p3]).then(() => {
                        next();
                        store.dispatch('stopLoadingPage');
                    });
                },
            },
        },
        {
            path: '/admin/questions',
            name: 'admin-questions',
            beforeEnter: guardAuth,
            component: loadView('admin/questions'),
            meta: {
                title: 'Vragen | Start to Stop',
                group: 'admin',
                beforeResolve(to: any, from: any, next: any) {
                    store.dispatch('startLoadingPage');

                    next();

                    store.dispatch('questions/index').finally(() => {
                        store.dispatch('stopLoadingPage');
                    });
                },
            },
        },
        {
            path: '/admin/questions/create',
            name: 'admin-questions-create',
            beforeEnter: guardAuth,
            component: loadView('admin/questions-detail'),
            meta: {
                title: 'Vragen aanmaken | Start to Stop',
                group: 'admin',
                beforeResolve(to: any, from: any, next: any) {
                    store.dispatch('startLoadingPage');

                    next();

                    store.dispatch('questions/clearViewingTip').finally(() => {
                        store.dispatch('stopLoadingPage');
                    });
                },
            },
        },
        {
            path: '/admin/questions/:id',
            name: 'admin-questions-update',
            beforeEnter: guardAuth,
            component: loadView('admin/questions-detail'),
            meta: {
                title: 'Vragen aanpassen | Start to Stop',
                group: 'admin',
                beforeResolve(to: any, from: any, next: any) {
                    store.dispatch('startLoadingPage');

                    const p1 = store.dispatch('questions/read', to.params.id);
                    const p2 = store.dispatch('tests/index');
                    const p3 = store.dispatch('levels/index');

                    Promise.all([p1, p2, p3]).then(() => {
                        next();
                        store.dispatch('stopLoadingPage');
                    });
                },
            },
        },
        {
            path: '/auth/logout',
            name: 'logout',
            beforeEnter: guardLogout,
            component: loadView('guest/authenticate'),
            meta: {
                title: 'Login | Start to Stop',
                type: 'login',
                group: 'guest',
            },
        },
        {
            path: '/error/:code',
            name: 'error',
            component: loadView('general/error'),
        },
    ],
});

router.beforeResolve(async (routeTo: any, routeFrom: any, next: any) => {
    try {
        if (routeFrom.meta && routeFrom.meta.leaveCallback) {
            await routeFrom.meta.leaveCallback(routeTo, routeFrom);
        }

        for (const route of routeTo.matched) {
            await new Promise((resolve, reject) => {
                if (route.meta && route.meta.title) {
                    // @ts-ignore
                    document.title = i18n.messages[i18n.locale].router[route.name] ? i18n.messages[i18n.locale].router[route.name] + ' | Start to Stop' : route.meta.title;
                }
                if (route.meta && route.meta.group) {
                    // @ts-ignore
                    document.querySelector('body').classList.add(route.meta.group);
                }

                if (route.meta && route.meta.beforeResolve) {
                    route.meta.beforeResolve(routeTo, routeFrom, (...args: any) => {
                        if (args.length) {
                            next(...args);
                            reject(new Error('Redirected'));
                        } else {
                            resolve({ success: true });
                        }
                    });
                } else {
                    resolve({ success: true });
                }
            });
        }
    } catch (error) {
        return;
    }

    next();
});

/**
 * Guard guest middleware
 * Redirect to the login page when the user is not logged in
 *
 * @param {to} string The route the user was comming from
 * @param {from} string The route the user is going to
 * @param {next} function The next route funciton
 */
async function guardGuestOrAuth(to: any, from: any, next: any) {
    try {
        let name: string = 'welcome';
        const auth: Auth = await store.dispatch('user/me');
        if (auth && auth.id) {

            if (auth.user_id && auth.consultant_id) {
                return next();
            } else if (auth.consultant_id) {
                name = 'admin-clients';
            } else if (auth.user_id) {
                name = 'dashboard';
            }
        }

        next({ name });
    } catch (e) {
        next({ name: 'welcome' });
    }
}

/**
 * Guard guest middleware
 * Redirect to the login page when the user is not logged in
 *
 * @param {to} string The route the user was comming from
 * @param {from} string The route the user is going to
 * @param {next} function The next route funciton
 */
async function guardGuest(to: any, from: any, next: any) {
    let auth: Auth | false = false;
    try {
        let name: string = 'welcome';
        auth = await store.dispatch('user/me');

        if (auth && auth.id) {

            if (to.name === 'email-confirm' && to.query.hasOwnProperty('email') && to.query.email === auth.email) {
                const emailConfirmPayload: EmailConfirmPayload = { email: to.query.email, confirmation_token: to.query.token };
                await store.dispatch('user/emailConfirm', emailConfirmPayload);
                await store.dispatch('user/me', true);
            }

            if (auth.user_id) {
                await store.dispatch('user/read', auth.user_id);
                name = 'timeline';
            }

            if (auth.consultant_id) {
                await store.dispatch('consultant/read', auth.consultant_id);
                name = 'admin-clients';
            }
        }

        next({ name });
    } catch (e) {
        if (auth && auth.id && to.name === 'email-confirm') {
            next({ name: 'auth-email-confirm' });
        } else {
            next();
        }
    }
}

/**
 * Guard auth middleware
 * Redirect to the login page when the user is not logged in
 *
 * @param {to} string The route the user was comming from
 * @param {from} string The route the user is going to
 * @param {next} function The next route funciton
 */
async function guardAuth(to: any, from: any, next: any) {
    try {
        const auth: Auth = await store.dispatch('user/me');

        if ((to.meta.group === 'auth' && !auth.user_id) || (to.meta.group === 'admin' && !auth.consultant_id)) {
            next({ name: 'login' });
        }

        if (auth.user_id) {
            store.dispatch('user/read', auth.user_id);
        }

        if (auth.consultant_id) {
            store.dispatch('consultant/read', auth.consultant_id);
        }
        next();
    } catch (e) {
        next({ name: 'login' });
    }
}

/**
 * Guard logout middleware
 * Redirect to the login page when the user is not logged in
 *
 * @param {to} string The route the user was comming from
 * @param {from} string The route the user is going to
 * @param {next} function The next route funciton
 */
function guardLogout(to: any, from: any, next: any) {

    store.dispatch('reset').then(() => {
        store.dispatch('user/logout').then(() => {
            next({ name: 'login' });
        });
    });

}

function loadView(view: string) {
    return () => lazyLoadView(import(`@/views/${view}.vue`));
}

function handlePageError(err: ErrorResponse, next: any) {
    next({ name: 'error', params: { code: err.status } });
}

/**
 * Lazy-loads view components, but with better UX. A loading view
 * will be used if the component takes a while to load, falling
 * back to a timeout view in case the page fails to load. You can
 * use this component to lazy-load a route with:
 *
 * component: () => lazyLoadView(import('@views/my-view'))
 *
 * @param {AsyncView} The view component
 */
function lazyLoadView(AsyncView: any) {
    const AsyncHandler = () => ({
        component: AsyncView,
        delay: 400,
        timeout: 10000,
    });

    return Promise.resolve({
        functional: true,
        render(h: CreateElement, { data, children }: any) {
            return h(AsyncHandler, data, children);
        },
    });
}

export default router;
